import { useCallback, useEffect, useMemo, useState } from 'react'
import kitApi, {
    KitCoreSignalJobDetailsDto,
    KitCoreSignalJobDetailsFilter,
    KitCoreSignalJobDto,
} from '@/api/kit/KitApi'
import { formatDateAllInfo } from '@/utils/dateUtils'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import Dropdown, {
    Item as DropdownItem,
} from '@/uiV2/common/form/inputs/DropDown'
import KitPaginationControls from './KitPaginationControls'

const PAGE_SIZE = 100
const CORE_SIGNAL_JOBS_TABLE_CLASS =
    'main-table w-full min-w-[1200px] text-[11px] leading-tight'
const CORE_SIGNAL_JOBS_HEADER_CELL_CLASS =
    'px-2 py-1 text-left align-top whitespace-normal'
const CORE_SIGNAL_JOBS_CELL_CLASS = 'px-2 py-1 align-top'
const JOB_DETAILS_FILTER_ITEMS: DropdownItem[] = [
    { id: 'all', content: 'All' },
    { id: 'has_details', content: 'Has Job Details' },
    { id: 'no_details', content: 'No Job Details' },
]

const formatOptionalDate = (value: string | null): string => {
    if (!value) {
        return '—'
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        return value
    }

    return formatDateAllInfo(parsed)
}

const parseDetailsFilter = (value?: string): KitCoreSignalJobDetailsFilter => {
    if (value === 'has_details') {
        return 'has_details'
    }

    if (value === 'no_details') {
        return 'no_details'
    }

    return 'all'
}

export default function KitCoreSignalJobsTab(): React.JSX.Element {
    const [jobs, setJobs] = useState<KitCoreSignalJobDto[]>([])
    const [pageNumber, setPageNumber] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasRequested, setHasRequested] = useState(false)
    const [detailsFilter, setDetailsFilter] =
        useState<KitCoreSignalJobDetailsFilter>('all')
    const [detailsLoadingJobId, setDetailsLoadingJobId] = useState<
        string | null
    >(null)
    const [selectedJobDetails, setSelectedJobDetails] =
        useState<KitCoreSignalJobDetailsDto | null>(null)

    const fetchJobs = useCallback(
        async (page: number, detailsFilter: KitCoreSignalJobDetailsFilter) => {
            try {
                setIsLoading(true)
                setError(null)

                const res = await kitApi.getCoreSignalJobs({
                    pageNumber: page,
                    perPage: PAGE_SIZE,
                    detailsFilter,
                })

                setJobs(res.jobs)
                setHasMore(res.hasMore)
            } catch (err) {
                console.error('Failed to load core signal jobs', err)
                setError('Unable to load core signal jobs.')
            } finally {
                setIsLoading(false)
                setHasRequested(true)
            }
        },
        [],
    )

    useEffect(() => {
        void fetchJobs(pageNumber, detailsFilter)
    }, [detailsFilter, fetchJobs, pageNumber])

    const selectedDetailsFilterItem = useMemo(
        () =>
            JOB_DETAILS_FILTER_ITEMS.find(
                (item) => item.id === detailsFilter,
            ) ?? JOB_DETAILS_FILTER_ITEMS[0],
        [detailsFilter],
    )

    const handleOpenDetails = useCallback(
        async (job: KitCoreSignalJobDto, fetchIfMissing: boolean) => {
            try {
                setError(null)
                setDetailsLoadingJobId(job.id)

                const res = await kitApi.getCoreSignalJobDetails({
                    coreSignalJobId: job.id,
                    fetchIfMissing,
                })

                setSelectedJobDetails(res.job)
                setJobs((prevJobs) =>
                    prevJobs.map((existingJob) =>
                        existingJob.id === job.id
                            ? {
                                  ...existingJob,
                                  title: res.job.title ?? existingJob.title,
                                  location:
                                      res.job.location ?? existingJob.location,
                                  companyName:
                                      res.job.companyName ??
                                      existingJob.companyName,
                                  coreSignalJobCreatedAt:
                                      res.job.coreSignalJobCreatedAt ??
                                      existingJob.coreSignalJobCreatedAt,
                                  hasJobDetails: res.job.hasJobDetails,
                              }
                            : existingJob,
                    ),
                )
            } catch (err) {
                console.error('Failed to load core signal job details', err)
                setError('Unable to load core signal job details.')
            } finally {
                setDetailsLoadingJobId(null)
            }
        },
        [],
    )

    const tableRows = useMemo(
        () =>
            jobs.map((job) => {
                const isLoadingDetails = detailsLoadingJobId === job.id

                return (
                    <tr key={job.id}>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {job.id}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {job.coreSignalId}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {job.companyName ?? '—'}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {job.title ?? '—'}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {job.location ?? '—'}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {formatOptionalDate(job.coreSignalJobCreatedAt)}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {job.hasJobDetails ? (
                                <button
                                    type="button"
                                    className="text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                                    onClick={() =>
                                        void handleOpenDetails(job, false)
                                    }
                                    disabled={isLoadingDetails}
                                >
                                    {isLoadingDetails ? 'Loading...' : 'View'}
                                </button>
                            ) : (
                                <Button
                                    type="button"
                                    style="secondary"
                                    size="small"
                                    onClick={() =>
                                        void handleOpenDetails(job, true)
                                    }
                                    enabled={!isLoadingDetails}
                                >
                                    {isLoadingDetails
                                        ? 'Loading...'
                                        : 'Get Details'}
                                </Button>
                            )}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {formatDateAllInfo(job.createdAt)}
                        </td>
                        <td className={CORE_SIGNAL_JOBS_CELL_CLASS}>
                            {formatDateAllInfo(job.updatedAt)}
                        </td>
                    </tr>
                )
            }),
        [detailsLoadingJobId, handleOpenDetails, jobs],
    )

    const closeDetailsModal = useCallback(() => {
        setSelectedJobDetails(null)
    }, [])

    const formattedDetailJson = useMemo(() => {
        if (!selectedJobDetails?.jobDetail) {
            return null
        }

        return JSON.stringify(selectedJobDetails.jobDetail, null, 2)
    }, [selectedJobDetails])

    const onDetailsFilterChange = useCallback((item: DropdownItem) => {
        setDetailsFilter(parseDetailsFilter(item.id))
        setPageNumber(1)
    }, [])

    return (
        <div>
            <div className="flex items-center justify-between">
                <h3 className="page-title">Core Signal Jobs</h3>
                <Button
                    type="button"
                    style="secondary"
                    onClick={() => void fetchJobs(pageNumber, detailsFilter)}
                    enabled={!isLoading}
                >
                    Refresh
                </Button>
            </div>
            <div className="mt-2 max-w-xs">
                <Dropdown
                    id="kit-core-signal-job-details-filter"
                    name="Job Details Filter"
                    placeholder="Select details filter"
                    items={JOB_DETAILS_FILTER_ITEMS}
                    selected={selectedDetailsFilterItem}
                    onChange={onDetailsFilterChange}
                />
            </div>
            {error && (
                <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                </div>
            )}
            {isLoading && !hasRequested ? (
                <p className="mt-4 text-sm text-gray-500">
                    Loading core signal jobs...
                </p>
            ) : jobs.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">
                    {hasRequested
                        ? 'No core signal jobs found.'
                        : 'Select this tab to load core signal jobs.'}
                </p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className={CORE_SIGNAL_JOBS_TABLE_CLASS}>
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide">
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Job Id
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Core Signal Id
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Company Name
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Job Title
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Location
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Core Signal Created At
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Job Details
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Created At
                                    </th>
                                    <th
                                        className={
                                            CORE_SIGNAL_JOBS_HEADER_CELL_CLASS
                                        }
                                    >
                                        Updated At
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{tableRows}</tbody>
                        </table>
                    </div>
                    <KitPaginationControls
                        pageNumber={pageNumber}
                        itemCount={jobs.length}
                        perPage={PAGE_SIZE}
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onPrevious={() =>
                            setPageNumber((page) => Math.max(1, page - 1))
                        }
                        onNext={() => setPageNumber((page) => page + 1)}
                    />
                </>
            )}
            {selectedJobDetails && (
                <Modal
                    title={`Core Signal Job Details - ${selectedJobDetails.id}`}
                    onClose={closeDetailsModal}
                    size="large"
                >
                    <div className="space-y-4 text-sm">
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            <div>
                                <span className="font-semibold">Job Id:</span>{' '}
                                {selectedJobDetails.id}
                            </div>
                            <div>
                                <span className="font-semibold">
                                    Core Signal Id:
                                </span>{' '}
                                {selectedJobDetails.coreSignalId}
                            </div>
                            <div>
                                <span className="font-semibold">Company:</span>{' '}
                                {selectedJobDetails.companyName ?? '—'}
                            </div>
                            <div>
                                <span className="font-semibold">Title:</span>{' '}
                                {selectedJobDetails.title ?? '—'}
                            </div>
                            <div>
                                <span className="font-semibold">Location:</span>{' '}
                                {selectedJobDetails.location ?? '—'}
                            </div>
                            <div>
                                <span className="font-semibold">
                                    Details Fetched At:
                                </span>{' '}
                                {formatOptionalDate(
                                    selectedJobDetails.jobDetailFetchedAt,
                                )}
                            </div>
                        </div>
                        {!formattedDetailJson ? (
                            <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-yellow-700">
                                No stored job details available for this job.
                            </div>
                        ) : (
                            <pre className="max-h-[60vh] overflow-auto rounded border bg-gray-50 p-3 text-xs leading-relaxed">
                                {formattedDetailJson}
                            </pre>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    )
}
